import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Sparkles } from 'lucide-react';
import { API } from '../config';
import { MobileNav } from '../components/MobileNav';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);
const dateFmt = (iso) => iso ? new Date(iso).toLocaleString('id-ID') : '—';

const TIER_COLORS = {
  gold: '#D4AF37', silver: '#C0C0C0', bronze: '#CD7F32',
  common: '#A3B8AC', last: '#EF4444',
};

const FALLBACK_PRIZE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23202824" width="200" height="200"/><text x="100" y="100" fill="%23A3B8AC" font-family="sans-serif" font-size="13" text-anchor="middle" dy=".3em">No image</text></svg>';

export const KujiHistory = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kuji/history`, { withCredentials: true });
        setItems(data || []);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="kuji-history-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/kuji')} className="flex items-center gap-2" data-testid="kuji-history-back">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
            <span style={{ color: '#F8FAF9' }}>Kuji</span>
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6" style={{ color: '#D4AF37' }} />
            <h1 className="text-xl md:text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>My Draws</h1>
          </div>
          <span className="text-sm" style={{ color: '#A3B8AC' }}>{items.length} draw{items.length !== 1 ? 's' : ''}</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-20 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No draws yet.</p>
            <button onClick={() => navigate('/kuji')} className="mt-4 px-6 py-2 rounded-full text-sm font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }}>
              Explore Kuji
            </button>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="kuji-history-grid">
            {items.map((d) => (
              <HistoryItem key={d.id} draw={d} />
            ))}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};

const HistoryItem = ({ draw }) => {
  const tierColor = TIER_COLORS[draw.prize_tier_snapshot] || '#F8FAF9';
  const isLast = !!draw.is_last_prize;
  return (
    <div
      className="rounded-xl overflow-hidden backdrop-blur-xl"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: isLast ? '2px solid #EF4444' : '1px solid rgba(255,255,255,0.1)',
      }}
      data-testid={`kuji-history-item-${draw.id}`}
    >
      <img
        src={draw.prize_image_snapshot || FALLBACK_PRIZE}
        alt={draw.prize_name_snapshot}
        className="w-full aspect-square object-cover"
        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
      />
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <Trophy className="w-3 h-3" style={{ color: tierColor }} />
          <span className="text-[10px] font-semibold tracking-wider" style={{ color: tierColor }}>
            {draw.prize_tier_snapshot?.toUpperCase()}
          </span>
          {isLast && <span className="ml-auto text-[10px] font-bold" style={{ color: '#EF4444' }}>★ LAST</span>}
        </div>
        <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{draw.prize_name_snapshot}</p>
        <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{draw.event_name_snapshot}</p>
        <p className="text-[10px] mt-1" style={{ color: '#A3B8AC' }}>{dateFmt(draw.created_at)}</p>
        <p className="text-[10px]" style={{ color: '#D4AF37' }}>Slot #{draw.slot_number} · {fmt(draw.coin_cost)} coins</p>
      </div>
    </div>
  );
};
